// super-excalidraw: dialog for editing per-item descriptions on the
// server-synced library. The descriptions are what the AI generation
// backend reads to know when to use which shape, so writing good ones
// is the high-leverage activity for AI-assisted diagramming.

import React, { useEffect, useState } from "react";

import { Dialog } from "@excalidraw/excalidraw/components/Dialog";

import {
  fetchLibrary,
  patchLibraryItem,
  type LibraryDTO,
  type LibraryItemDTO,
} from "../data/library";

import "./ManageLibraryDialog.scss";

type Props = {
  onClose: () => void;
};

export const ManageLibraryDialog: React.FC<Props> = ({ onClose }) => {
  const [lib, setLib] = useState<LibraryDTO | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, { name: string; description: string }>>({});

  useEffect(() => {
    fetchLibrary()
      .then((data) => {
        setLib(data);
        const d: typeof drafts = {};
        data.items.forEach((it) => {
          d[it.id] = {
            name: it.name ?? "",
            description: it.description ?? "",
          };
        });
        setDrafts(d);
      })
      .catch((e) => setError(e.message ?? String(e)));
  }, []);

  const onSaveOne = async (item: LibraryItemDTO) => {
    const draft = drafts[item.id];
    if (!draft) {
      return;
    }
    setSavingId(item.id);
    try {
      await patchLibraryItem(item.id, {
        name: draft.name,
        description: draft.description,
      });
      // Refresh from server so timestamps stay consistent
      const fresh = await fetchLibrary();
      setLib(fresh);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Dialog onCloseRequest={onClose} title="Manage Library" size="wide">
      <div className="manage-lib">
        <p className="manage-lib__intro">
          Server-synced library items (personal + imported). Add a short
          <em> description</em> so the AI knows when to reach for each shape.
        </p>
        {error && <div className="manage-lib__error">Error: {error}</div>}
        {lib === null && <div className="manage-lib__loading">Loading…</div>}
        {lib?.items.length === 0 && (
          <div className="manage-lib__empty">
            No library items yet. Add shapes to your library in Excalidraw
            (right toolbar) and they'll appear here.
          </div>
        )}
        <ul className="manage-lib__list">
          {lib?.items.map((item) => {
            const draft = drafts[item.id] ?? { name: "", description: "" };
            return (
              <li key={item.id} className="manage-lib__row">
                <div className="manage-lib__fields">
                  <input
                    className="manage-lib__name"
                    placeholder="Name (e.g. Lambda)"
                    value={draft.name}
                    onChange={(e) =>
                      setDrafts((d) => ({
                        ...d,
                        [item.id]: { ...draft, name: e.target.value },
                      }))
                    }
                  />
                  <textarea
                    className="manage-lib__desc"
                    placeholder="Description for AI (e.g. AWS serverless function — orange square with λ)"
                    rows={2}
                    value={draft.description}
                    onChange={(e) =>
                      setDrafts((d) => ({
                        ...d,
                        [item.id]: { ...draft, description: e.target.value },
                      }))
                    }
                  />
                </div>
                <button
                  type="button"
                  className="manage-lib__save"
                  disabled={savingId === item.id}
                  onClick={() => onSaveOne(item)}
                >
                  {savingId === item.id ? "…" : "Save"}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Dialog>
  );
};
